import type { Props as VariantSelectionProps } from '#commerce/components/dialog/variant-selection/VariantSelection.vue'
import type { BaseForm as BaseFormType } from '#components'
import type { ContentProductEspots } from '#root/api/clients/content/data-contracts'
import type {
  InventoryData,
  ProductAttributesOptionsColor,
  ProductPrice,
  ProductReviewsQuestionsResponse,
  ProductReviewsRollupProperty
} from '#root/api/clients/product/data-contracts'
import type { Props as PickupInStoreProps } from '#types/components/product/pickupInStore'
import type { ProductQuestionsParams, ProductReviewsData, ProductReviewsParams } from '#types/components/product/reviews'
import type { InteractionOrigin, ProductContext } from '#types/gtm'
import type { ImageItem, SketchfabItem, VideoItem } from '#types/media'
import type { BaseNotification } from '#types/notification'
import type {
  AttributeSelection,
  ProductAttribute,
  ProductAttributeType,
  ProductExtended,
  SelectedVariant
} from '#types/product'
import type { InjectionKey } from 'vue'

interface PdpContext {
  addToCart: (payload?: any) => Promise<boolean>
  addToCartAndCheckout: (payload: any) => Promise<void>
  addToCartLoading: Ref<boolean>
  attributeSelection: AttributeSelection
  attributeSelectionFormRef: Ref<InstanceType<typeof BaseFormType> | null>
  attributeSelectionLabels: Ref<any>
  checkoutCtaLoading: Ref<boolean>
  defaultVariant: ComputedRef<SelectedVariant>
  dialogNotification: BaseNotification
  eSpots: ComputedRef<ContentProductEspots['sectionsMap'] | undefined>
  error: Ref<boolean>
  eyebrowStyle: Ref<any>
  findInStore: (attributeSelectionLabels: Record<string, { label: string, value: string }>, mode?: PickupInStoreProps['mode']) => Promise<void>
  fitRating: Ref<ProductReviewsRollupProperty | undefined>
  galleryItems: Ref<(ImageItem | VideoItem | SketchfabItem)[]>
  getAttrOptionAvailable: (type: ProductAttributeType, value: string) => boolean | undefined
  getAttrOptionNotifyMe: (type: ProductAttributeType, value: string) => boolean | undefined
  handleNextAttribute: (options?: Omit<VariantSelectionProps, 'attribute'>, callback?: () => void) => void
  inventory: Ref<InventoryData | null>
  inventoryError: Ref<Error | null>
  isComingSoon: Ref<boolean>
  isDigitalProduct: Ref<boolean>
  isFavorite: Ref<boolean>
  isFavoritePending: Ref<boolean>
  isNonLoyaltyMember: Ref<boolean>
  isNotifyMe: ComputedRef<boolean | undefined>
  isOutOfStock: Ref<boolean>
  isProductOrInventoryPending: ComputedRef<boolean>
  isRobisOnly: Ref<boolean | undefined>
  isSelectedProductOutOfStock: Ref<boolean | undefined>
  isSignInToBuy: Ref<boolean>
  isSomeVariantNotifyMe: Ref<boolean | undefined>
  nextAttribute: Ref<ProductAttribute | undefined>
  noVariantSelected: () => boolean
  onZoom: (index: number, zoomFactor: number) => void
  openQuickshop: (payload: any, gtmConfig?: ProductContext & { appSource?: InteractionOrigin }) => Promise<any>
  openShopTheLook: (payload: any) => void
  openSizeGuide: (options?: { showFitRating?: boolean, keep?: boolean }) => void
  pending: Ref<boolean>
  product: Ref<ProductExtended | null>
  productColor: Ref<ProductAttributesOptionsColor | null>
  productError: Ref<Error | null>
  productId: Ref<string>
  productPending: Ref<boolean>
  productPrice: Ref<ProductPrice | undefined>
  questionsData: Ref<ProductReviewsQuestionsResponse | null>
  questionsError: Ref<Error | null>
  questionsPending: Ref<boolean>
  questionsQuery: ProductQuestionsParams
  resetNotification: () => void
  reviewsData: Ref<ProductReviewsData | undefined>
  reviewsError: Ref<Error | null>
  reviewsPending: Ref<boolean>
  reviewsQuery: ProductReviewsParams
  selectColor: (_id: any, url: any, colorLabel?: string) => Promise<void>
  selectSize: (type: any, value: any, label?: string) => void
  selectedVariant: Ref<SelectedVariant>
  setNotification: (payload: any) => void
  shippingMethod: Ref<'standard' | 'pickup' | undefined>
  showSizeAndFit: Ref<boolean>
  toggleFavorites: (productColor?: string) => Promise<void>
  triggerApplePaySession: () => void
  tryItOn: () => unknown
}

export const PdpContextKey: InjectionKey<PdpContext> = Symbol('PdpContext')
